/**
 * buildEntityDetails — what an inspector shows for ONE entity, as pure data.
 *
 * The companion of `buildNavTree`, and for the same reason: the viewer's
 * inspector grew inside React (`Sidebar.tsx`'s `ModuleDetails` reading store
 * state and board-layout indexes), so no other host could show more than the
 * nav tree's bare structure — a native IDE panel ended up listing kind, id and
 * children while the web board showed description, fields, aspects, call
 * relationships and processes for the same module.
 *
 * So the derivation lives here, over a `ResolvedModel` plus the engine's
 * `DependencyGraph`, with no DOM and no store. The LSP answers a details
 * request with it, which also decouples the panel from the diagram: a host can
 * fill its inspector from a tree selection with no board rendered anywhere.
 *
 * Addresses are the nav tree's addresses — `moduleDiffId` for modules and
 * `<moduleId>#<name>` for interfaces — because the id a host holds came FROM
 * the nav tree (or from a board selection, which mints the same one). Any
 * other spelling here would simply never match.
 */
import {
  isCallEdge,
  moduleDiffId,
  primaryFieldOf,
  processIdentity,
  valueText,
  type BacklinkIndex,
  type DecorationInstance,
  type DecorationRegistry,
  type DependencyGraph,
  type ResolvedInterface,
  type ResolvedModel,
  type ResolvedModule,
  type ResolvedSurface,
  type Value,
} from "@archlang/engine";
import { locationOf } from "./nav-tree.js";
import {
  callsBetweenPaths,
  inTree,
  interfaceOf,
  type CallAggregate,
} from "./call-aggregation.js";
import { decorationArgText } from "./serialize.js";
import type { NavLocation, NavNodeKind } from "./nav-tree.js";

/** A named value pair — one field, one aspect. */
export interface DetailPair {
  readonly key: string;
  readonly value: string;
}

/** One end of a call relationship, as an inspector lists it. */
export interface DetailConnection {
  readonly peerId: string;
  readonly peerLabel: string;
  /** How many distinct calls make up this relationship. */
  readonly count: number;
  /**
   * The interfaces the relationship actually goes through, in call order.
   *
   * A bare peer name says two modules are coupled; WHICH interface says
   * whether that coupling is one narrow call or the whole surface. The web
   * inspector lists every call as `from → interface` for the same reason, and
   * a panel that only names the peer is the one that reads as poorer.
   */
  readonly via: readonly string[];
  /** The peer's type, when the graph knows it — same role as
   *  {@link DetailRef.typeName}. */
  readonly peerTypeName?: string;
}

export interface DetailInterface {
  readonly name: string;
  readonly typeName?: string;
  readonly description?: string;
  /** Set when the interface is reached through a nested surface rather than
   *  declared on the module directly. */
  readonly viaSurface?: string;
  /** Calls that land on this interface. An interface nobody calls is either
   *  dead or the next thing to wire up, and neither is visible without it. */
  readonly usage: number;
}

export interface DetailRef {
  readonly id: string;
  readonly label: string;
  /** The referenced entity's type (`service`, `queue`, plain `module` …) —
   *  what lets a host draw the same typed glyph the nav tree does. */
  readonly typeName?: string;
}

/**
 * What an inspector can be pointed at.
 *
 * A CONNECTION is not a nav node — you cannot list it in a tree, because it
 * belongs to two modules at once — but it is very much selectable on a board,
 * and a panel that answers "nothing is selected" when you click an arrow is
 * the panel people stop trusting.
 */
export type DetailKind = NavNodeKind | "connection" | "aspect";

/**
 * Something that points AT this entity with a `[[Name]]` reference — another
 * element's description, or a `.md` document.
 *
 * The inverse of the description text, and the half you cannot get by reading
 * the entity itself: "what have we written about this" is a different question
 * from "what does this say about itself", and only the index can answer it.
 */
export interface DetailBacklink {
  readonly kind: "element" | "document";
  readonly label: string;
  /** Qualified name for an element, source path for a document. */
  readonly detail?: string;
}

/**
 * A `@planned` / `@deprecated` window written on the entity.
 *
 * Without this, a decoration is invisible unless you already know to look for
 * `@` in the source — so a deprecated module reads as a perfectly ordinary one,
 * and the `use:` pointer the author left (the entire reason for writing it)
 * reaches nobody.
 */
export interface DetailLifecycle {
  /** `planned` or `deprecated`. */
  readonly name: string;
  readonly from?: string;
  readonly to?: string;
  /** `deprecated` only: what to use instead, and why it was retired. */
  readonly use?: string;
  readonly reason?: string;
}

/** One interface a connection goes through, and how its calls arise. */
export interface DetailCall {
  /** The callee interface, as the callee names it (`reserve`, not
   *  `Inventory.reserve` — the owner is already the other endpoint). */
  readonly interfaceName: string;
  readonly count: number;
  /** Processes that place these calls, by name. A call nothing drives is a
   *  dependency with no story behind it, which is worth seeing. */
  readonly processes: readonly string[];
}

export interface EntityDetails {
  readonly id: string;
  readonly kind: DetailKind;
  readonly label: string;
  /** Dotted path, when it says more than the label alone does. */
  readonly qualifiedName?: string;
  readonly typeName?: string;
  readonly description?: string;
  readonly team?: string;
  readonly parent?: DetailRef;
  readonly fields: readonly DetailPair[];
  readonly aspects: readonly DetailPair[];
  readonly interfaces: readonly DetailInterface[];
  readonly children: readonly DetailRef[];
  readonly callsOut: readonly DetailConnection[];
  readonly calledBy: readonly DetailConnection[];
  /**
   * Calls the module makes to ITSELF, kept apart from both directions above.
   *
   * Not a relationship with anything — which is why it must not sit in
   * `callsOut`, where it would read as coupling — but not noise either: it is
   * how much of the module's own surface is load-bearing internally.
   */
  readonly internal: readonly DetailConnection[];
  readonly processes: readonly DetailRef[];
  readonly location?: NavLocation;
  readonly synthesized?: boolean;
  /** For an INTERFACE: how many calls land on it. */
  readonly usage?: number;
  /** For a CONNECTION: the two modules it joins, caller first. */
  readonly endpoints?: { readonly from: DetailRef; readonly to: DetailRef };
  /** For a CONNECTION: the interfaces it goes through. */
  readonly calls?: readonly DetailCall[];
  /** Descriptions and documents that mention this entity by `[[Name]]`. */
  readonly mentionedIn: readonly DetailBacklink[];
  /** `@planned` / `@deprecated` windows written on it. */
  readonly lifecycle: readonly DetailLifecycle[];
  /** For a MODULE: the name was derived from the type (`service {}`), not
   *  authored — the web inspector's "derived" chip. */
  readonly unnamed?: boolean;
  /** For a PROCESS: how many top-level actions it declares. */
  readonly stepCount?: number;
  /** For a PROCESS: the modules its calls touch, either end. */
  readonly involves?: readonly DetailRef[];
  /** For an ASPECT pill: the classification it stands for. Members are in
   *  `children` — the same slot a module's containment uses. */
  readonly aspect?: { readonly key: string; readonly value: string };
}

export interface BuildDetailsOptions {
  /**
   * The workspace's `[[Name]]` index, for the "Mentioned in" list.
   *
   * Passed in rather than derived: it is built once per assemble over every
   * description and `.md` body in the package, and rebuilding it per selection
   * would put the whole corpus on the inspector's hot path. Omit it and the
   * list is simply empty — the rest of the details are unaffected.
   */
  readonly backlinks?: BacklinkIndex;
}

/** A module found by address, with everything its details need from the walk. */
interface Located {
  readonly mod: ResolvedModule;
  readonly path: readonly string[];
  readonly parentPath: readonly string[];
  readonly parent: ResolvedModule | null;
  readonly parentParentPath: readonly string[];
}

// Display form: a string atom shows its CONTENT, not its source spelling —
// the web inspector renders `team: "Commerce"` as `Commerce`, and quoting it
// here would make the native panel the one that reads differently.
const text = (v: Value | undefined): string =>
  v ? (v.kind === "string" ? v.text : valueText(v)) : "";

/** Spread-friendly: omit the key entirely rather than carrying `undefined`. */
const locationField = (loc: NavLocation | undefined): { location?: NavLocation } =>
  loc ? { location: loc } : {};

function pairs(
  entries: readonly {
    readonly path: readonly string[];
    readonly value?: Value;
    readonly drop?: boolean;
  }[],
): DetailPair[] {
  return entries
    .filter(
      (e) =>
        // A `drop` entry REMOVES an inherited key rather than carrying a value
        // of its own — listing it would show a phantom row with no value.
        !e.drop &&
        // `widget.*` is how the type tells a board how to DRAW the module
        // (icon, colour, size). It is inherited onto every instance, so it
        // would bury the module's own fields under render config on every
        // single entity. The web inspector hides it for the same reason.
        e.path[0] !== "widget",
    )
    .map((e) => ({ key: e.path.join("."), value: text(e.value) }));
}

/** A single-segment key's value — `team`, not `team.lead`. */
function own(
  entries: readonly { readonly path: readonly string[]; readonly value?: Value }[],
  key: string,
): Value | undefined {
  return entries.find((e) => e.path.length === 1 && e.path[0] === key)?.value;
}

/**
 * The lifecycle windows among an element's decorations.
 *
 * `@planned`/`@deprecated` are the two the language gives meaning to; every
 * other decoration is project vocabulary and belongs with the fields, not in a
 * section that promises a schedule.
 */
function lifecycleOf(
  decorations: readonly DecorationInstance[] | undefined,
  registry: DecorationRegistry | undefined,
): DetailLifecycle[] {
  const arg = (inst: DecorationInstance, name: string): string | undefined => {
    const named = inst.args.find((a) => a.name === name);
    if (named) return decorationArgText(named.value);
    // The single bare positional binds the definition's PRIMARY field — the
    // first declared one, `from` for both lifecycle decorations, so a lone
    // marker always names where the transition STARTS (`@planned(v3)` arrives
    // at v3; `@deprecated(v3)` sunsets from v3).
    //
    // Read that off the registry rather than naming a field here: this said
    // `to` for years and silently kept saying it when `@planned` lost its
    // second field, so the panel announced the opposite of the source. A model
    // assembled without a registry falls back to `from` — the builtin schema
    // both lifecycle decorations declare — so the marker still reads correctly
    // instead of vanishing from the panel.
    const decl = registry?.kinds.get(inst.name);
    if ((decl ? primaryFieldOf(decl) : "from") === name) {
      const bare = inst.args.find((a) => a.name === undefined);
      return bare ? decorationArgText(bare.value) : undefined;
    }
    return undefined;
  };
  const keep = (v: string | undefined, key: keyof DetailLifecycle) =>
    v ? { [key]: v } : {};
  return (decorations ?? [])
    .filter((d) => d.name === "planned" || d.name === "deprecated")
    .map((d) => ({
      name: d.name,
      ...keep(arg(d, "from"), "from"),
      ...keep(arg(d, "to"), "to"),
      ...(d.name === "deprecated"
        ? { ...keep(arg(d, "use"), "use"), ...keep(arg(d, "reason"), "reason") }
        : {}),
    }));
}

/** Depth-first walk for the module whose nav address is `id`. */
function locate(
  modules: readonly ResolvedModule[],
  id: string,
  parentPath: readonly string[],
  parent: ResolvedModule | null,
  parentParentPath: readonly string[],
): Located | null {
  for (const mod of modules) {
    const path = [...parentPath, mod.name];
    if (moduleDiffId(mod, parentPath) === id) {
      return { mod, path, parentPath, parent, parentParentPath };
    }
    const hit = locate(mod.modules, id, path, mod, parentPath);
    if (hit) return hit;
  }
  return null;
}

/** Interfaces declared on the module, then those its nested surfaces own —
 *  the surface's are the module's public shape too, just written one level in. */
function collectInterfaces(
  mod: ResolvedModule,
  usage: ReadonlyMap<string, number>,
): DetailInterface[] {
  const out: DetailInterface[] = mod.interfaces.map((iface) => one(iface, usage));
  const walkSurface = (surface: ResolvedSurface, via: string): void => {
    for (const iface of surface.interfaces) {
      out.push({ ...one(iface, usage), viaSurface: via });
    }
    for (const nested of surface.surfaces) walkSurface(nested, `${via}.${nested.name}`);
  };
  for (const surface of mod.surfaces) walkSurface(surface, surface.name);
  return out;
}

/** The ResolvedInterface for `name` on `mod` — direct, or owned by any
 *  (possibly nested) surface. The declaration lookalike of
 *  {@link collectInterfaces}' flattening. */
function findInterfaceDecl(
  mod: ResolvedModule,
  name: string,
): ResolvedInterface | undefined {
  const direct = mod.interfaces.find((i) => i.name === name);
  if (direct) return direct;
  const inSurface = (s: ResolvedSurface): ResolvedInterface | undefined => {
    const hit = s.interfaces.find((i) => i.name === name);
    if (hit) return hit;
    for (const nested of s.surfaces) {
      const deep = inSurface(nested);
      if (deep) return deep;
    }
    return undefined;
  };
  for (const s of mod.surfaces) {
    const hit = inSurface(s);
    if (hit) return hit;
  }
  return undefined;
}

function one(iface: ResolvedInterface, usage: ReadonlyMap<string, number>): DetailInterface {
  const description = iface.decl.body?.descriptions.map((d) => d.text).join("\n\n");
  return {
    name: iface.name,
    typeName: iface.typeName,
    usage: usage.get(iface.name) ?? 0,
    ...(description ? { description } : {}),
  };
}

interface Peer {
  label: string;
  count: number;
  via: string[];
  typeName?: string;
}

/** Collapse a per-peer accumulation into the wire shape — shared by the
 *  module buckets and the interface-callers split so the two can't drift. */
function peersToConnections(m: ReadonlyMap<string, Peer>): DetailConnection[] {
  return [...m].map(([peerId, v]) => ({
    peerId,
    peerLabel: v.label,
    count: v.count,
    via: v.via,
    ...(v.typeName ? { peerTypeName: v.typeName } : {}),
  }));
}

/**
 * Call relationships, counted per peer.
 *
 * Edges are per-CALL, so a module calling two of another's interfaces
 * contributes two edges to one relationship — collapsing them and keeping both
 * the count and the interfaces is what makes the list readable at architecture
 * scale without flattening away what the coupling actually is.
 *
 * Self-calls go to `internal`, not `callsOut`: a module is not coupled to
 * itself, and listing it among its dependencies makes every module look one
 * relationship busier than it is.
 */
function connections(
  graph: DependencyGraph | null,
  path: readonly string[],
): {
  callsOut: DetailConnection[];
  calledBy: DetailConnection[];
  internal: DetailConnection[];
  processes: DetailRef[];
  usageByInterface: Map<string, number>;
} {
  const callsOut = new Map<string, Peer>();
  const calledBy = new Map<string, Peer>();
  const internal = new Map<string, Peer>();
  const processes = new Map<string, string>();
  const usageByInterface = new Map<string, number>();
  const empty = {
    callsOut: [],
    calledBy: [],
    internal: [],
    processes: [],
    usageByInterface,
  };
  if (!graph) return empty;

  const me = path.join(".");
  const idOf = (modulePath: readonly string[]): string | null => {
    const mod = graph.modulesByPath.get(modulePath.join("."));
    if (!mod) return null;
    const parent = graph.pathOfModule.get(mod)?.slice(0, -1) ?? [];
    return moduleDiffId(mod, parent);
  };
  const typeOf = (modulePath: readonly string[]): string | undefined =>
    graph.modulesByPath.get(modulePath.join("."))?.typeName;
  const add = (
    bucket: Map<string, Peer>,
    key: string,
    label: string,
    via: string,
    typeName?: string,
  ): void => {
    const seen = bucket.get(key);
    if (seen) {
      seen.count += 1;
      if (via && !seen.via.includes(via)) seen.via.push(via);
    } else {
      bucket.set(key, { label, count: 1, via: via ? [via] : [], typeName });
    }
  };

  for (const edge of graph.edges) {
    if (!isCallEdge(edge)) continue;
    const from = edge.fromModulePath.join(".");
    const to = edge.toModulePath.join(".");
    if (from !== me && to !== me) continue;
    if (edge.processId) processes.set(edge.processId, edge.processName ?? edge.processId);
    const via = interfaceOf(edge);
    // Usage counts what lands ON this module, whoever placed the call — an
    // interface called only from inside is still called.
    if (to === me && via) {
      usageByInterface.set(via, (usageByInterface.get(via) ?? 0) + 1);
    }
    if (from === to) {
      // Minted like the other buckets: the dotted path matches no nav/board
      // address for a module with an authored #id or a landed space.
      add(internal, idOf(path) ?? me, path[path.length - 1] ?? me, via, typeOf(path));
      continue;
    }
    const peerPath = from === me ? edge.toModulePath : edge.fromModulePath;
    const bucket = from === me ? callsOut : calledBy;
    add(
      bucket,
      idOf(peerPath) ?? peerPath.join("."),
      peerPath[peerPath.length - 1] ?? peerPath.join("."),
      via,
      typeOf(peerPath),
    );
  }

  return {
    callsOut: peersToConnections(callsOut),
    calledBy: peersToConnections(calledBy),
    internal: peersToConnections(internal),
    processes: [...processes].map(([id, label]) => ({ id, label })),
    usageByInterface,
  };
}

/**
 * A board edge's address: `<space-parent>|<fromModule>→<toModule>`, minted by
 * scene's board layout. The endpoints are module keys from the SAME scheme as
 * {@link moduleDiffId}, which is what makes an arrow on a board resolvable
 * here at all — the board and the nav tree agree on what a module is called.
 *
 * The parent segment scopes the edge to the space it was drawn in; it says
 * nothing about which modules are involved, so it is parsed off and dropped.
 */
const CONNECTION_ADDRESS = /^(?:[^|]*)\|(.+)→(.+)$/;

/**
 * Details for a connection — the calls between two modules, which is what a
 * board arrow AGGREGATES. One arrow can be a single call or a dozen, and the
 * only way to tell is to list the interfaces it goes through.
 *
 * Answers null for anything that isn't a connection address, so the module and
 * interface paths below stay the ordinary case.
 */
function connectionDetails(
  model: ResolvedModel,
  graph: DependencyGraph | null,
  id: string,
): EntityDetails | null {
  const match = CONNECTION_ADDRESS.exec(id);
  if (!match) return null;
  const from = locate(model.modules, match[1]!, [], null, []);
  const to = locate(model.modules, match[2]!, [], null, []);
  if (!from || !to) return null;
  return oneWayConnection(graph, id, from, to);
}

/**
 * Every call between two ends of the board, keyed by `Located` rather than by
 * path — the spelling the inspector already has in hand. The walk itself, and
 * the subtree/`nested` rules that make it correct, live in `call-aggregation`
 * so the guided story's narration cannot answer this question differently.
 */
function callsBetween(
  graph: DependencyGraph | null,
  from: Located,
  to: Located,
): CallAggregate {
  return callsBetweenPaths(graph, from.path.join("."), to.path.join("."));
}

const endpointRef = (located: Located): DetailRef => ({
  id: moduleDiffId(located.mod, located.parentPath),
  label: located.mod.name,
  typeName: located.mod.typeName,
});

const callList = (
  byInterface: ReadonlyMap<string, { count: number; processes: Set<string> }>,
): DetailCall[] =>
  [...byInterface].map(([interfaceName, v]) => ({
    interfaceName,
    count: v.count,
    processes: [...v.processes],
  }));

/**
 * The connection panel for calls running one way, from `from` to `to`.
 *
 * `back` is the reverse traffic, when there is any: a mutual relationship is
 * ONE thing to look at, so rather than two panels it becomes one whose header
 * reads ⇄ and whose reverse half lands in `calledBy` — the field a reader
 * already scans for "and who calls this".
 */
function oneWayConnection(
  graph: DependencyGraph | null,
  id: string,
  from: Located,
  to: Located,
  back?: { peer: Located; byInterface: Map<string, { count: number; processes: Set<string> }> },
): EntityDetails | null {
  const { byInterface, processes } = callsBetween(graph, from, to);
  // An arrow the graph no longer has any call for: the model moved under a
  // stale selection. Better to say "not in the current model" than to draw an
  // empty connection panel that looks like a rendering bug.
  if (byInterface.size === 0) return null;

  const arrow = back ? "⇄" : "→";
  return {
    id,
    kind: "connection",
    label: `${from.mod.name} ${arrow} ${to.mod.name}`,
    qualifiedName: `${from.path.join(".")} ${arrow} ${to.path.join(".")}`,
    endpoints: { from: endpointRef(from), to: endpointRef(to) },
    calls: callList(byInterface),
    fields: [],
    aspects: [],
    interfaces: [],
    children: [],
    callsOut: [],
    calledBy: back
      ? [
          {
            peerId: endpointRef(back.peer).id,
            peerLabel: back.peer.mod.name,
            count: [...back.byInterface.values()].reduce((n, v) => n + v.count, 0),
            via: [...back.byInterface.keys()],
            peerTypeName: back.peer.mod.typeName,
          },
        ]
      : [],
    internal: [],
    processes: [...processes].map(([pid, label]) => ({ id: pid, label })),
    // A connection has no name of its own, so nothing can `[[reference]]` it,
    // and nothing can decorate it.
    mentionedIn: [],
    lifecycle: [],
  };
}

/**
 * A wall-port's address: `<owner>|port|<peer>` for a cross-space port,
 * `<owner>|boundary|<peer>` for a view-boundary stub, optionally `|v` on the
 * former for a telemetry violation. Both are pills a reader can click, so both
 * have to resolve; the difference is only WHY the peer isn't drawn — outside
 * this space, versus not projected into this view — and the answer to "what
 * runs between these two" is the same either way.
 *
 * Deliberately DIRECTIONLESS. A port's identity is (owner, peer) — one pill per
 * relationship — because a peer that is called and calls back is one
 * relationship read from two ends, not two. So the direction is not in the
 * address and cannot be; it is derived below from the calls the graph actually
 * holds, which is also the only answer that stays right after an edit.
 */
const PORT_ADDRESS = /^(.+)\|(?:port|boundary)\|(.+?)(?:\|v)?$/;

function portDetails(
  model: ResolvedModel,
  graph: DependencyGraph | null,
  id: string,
): EntityDetails | null {
  const match = PORT_ADDRESS.exec(id);
  if (!match) return null;
  const owner = locate(model.modules, match[1]!, [], null, []);
  const peer = locate(model.modules, match[2]!, [], null, []);
  if (!owner || !peer) return null;

  const out = callsBetween(graph, owner, peer);
  const back = callsBetween(graph, peer, owner);
  if (out.byInterface.size > 0) {
    return oneWayConnection(
      graph,
      id,
      owner,
      peer,
      back.byInterface.size > 0 ? { peer, byInterface: back.byInterface } : undefined,
    );
  }
  // Inbound only — orient it the way the calls run. Reporting owner → peer here
  // would draw an arrow pointing at the wrong module.
  return oneWayConnection(graph, id, peer, owner);
}

/** Empty relationship slate — the fields every EntityDetails must carry. */
const NO_RELATIONS = {
  fields: [] as DetailPair[],
  aspects: [] as DetailPair[],
  interfaces: [] as DetailInterface[],
  children: [] as DetailRef[],
  callsOut: [] as DetailConnection[],
  calledBy: [] as DetailConnection[],
  internal: [] as DetailConnection[],
  processes: [] as DetailRef[],
  mentionedIn: [] as DetailBacklink[],
  lifecycle: [] as DetailLifecycle[],
};

/**
 * An aspect pill's address, as the board mints it: `aspect:<key>=<json>` for
 * the shared hub, `…::rep::<member>` for a replicated per-member copy. Both
 * are selectable pills, and both mean the same classification — so the copy
 * suffix is parsed off and the answer is the hub's.
 */
const ASPECT_ADDRESS = /^aspect:(.+?)=(".*")(?:::rep::.*)?$/;

function aspectDetails(
  model: ResolvedModel,
  id: string,
  mentions: (name: string) => DetailBacklink[],
): EntityDetails | null {
  const match = ASPECT_ADDRESS.exec(id);
  if (!match) return null;
  const key = match[1]!;
  let value: string;
  try {
    const parsed: unknown = JSON.parse(match[2]!);
    if (typeof parsed !== "string") return null;
    value = parsed;
  } catch {
    return null;
  }
  // Every module carrying `aspect <key>: <value>` — the pill's member web.
  const members: DetailRef[] = [];
  const walk = (mods: readonly ResolvedModule[], parentPath: readonly string[]): void => {
    for (const mod of mods) {
      const hit = mod.body.aspects.some(
        (a) => a.path.join(".") === key && text(a.value) === value,
      );
      if (hit) {
        members.push({ id: moduleDiffId(mod, parentPath), label: mod.name, typeName: mod.typeName });
      }
      walk(mod.modules, [...parentPath, mod.name]);
    }
  };
  walk(model.modules, []);
  return {
    ...NO_RELATIONS,
    id,
    kind: "aspect",
    label: value,
    qualifiedName: `${key}: ${value}`,
    aspect: { key, value },
    children: members,
    mentionedIn: mentions(value),
  };
}

/** Details for a process id (`processIdentity` mint — the nav tree's address). */
function processDetails(
  model: ResolvedModel,
  graph: DependencyGraph | null,
  id: string,
  mentions: (name: string) => DetailBacklink[],
): EntityDetails | null {
  const proc = model.processes.find((p) => processIdentity(p) === id);
  if (!proc) return null;
  // Modules the process touches — either end of any call it derives. The web
  // inspector answers this per-module ("involved processes"); this is the same
  // index read from the process side.
  const involves = new Map<string, DetailRef>();
  for (const edge of graph?.edges ?? []) {
    if (!isCallEdge(edge) || edge.processId !== id) continue;
    for (const path of [edge.fromModulePath, edge.toModulePath]) {
      const mod = graph?.modulesByPath.get(path.join("."));
      if (!mod) continue;
      const parent = graph?.pathOfModule.get(mod)?.slice(0, -1) ?? [];
      const ref = { id: moduleDiffId(mod, parent), label: mod.name, typeName: mod.typeName };
      involves.set(ref.id, ref);
    }
  }
  return {
    ...NO_RELATIONS,
    id,
    kind: "process",
    label: proc.name,
    ...(proc.description ? { description: proc.description } : {}),
    fields: pairs(proc.fields ?? []),
    aspects: pairs(proc.aspects ?? []),
    stepCount: proc.steps.length,
    involves: [...involves.values()],
    mentionedIn: mentions(proc.name),
    lifecycle: lifecycleOf(proc.decorations, model.decorationRegistry),
    ...locationField(locationOf(proc.source)),
  };
}

/**
 * Who calls ONE interface, grouped by caller — the web inspector's "Used by"
 * list, which names the callers where `usage` only counts them. Self-calls
 * land in `internal`, same split as the module panel.
 */
function interfaceCallers(
  graph: DependencyGraph | null,
  ownerPath: readonly string[],
  name: string,
): { calledBy: DetailConnection[]; internal: DetailConnection[] } {
  const owner = ownerPath.join(".");
  const calledBy = new Map<string, Peer>();
  const internal = new Map<string, Peer>();
  for (const edge of graph?.edges ?? []) {
    if (!isCallEdge(edge)) continue;
    if (edge.toModulePath.join(".") !== owner || interfaceOf(edge) !== name) continue;
    const from = edge.fromModulePath.join(".");
    const bucket = from === owner ? internal : calledBy;
    const label = edge.fromModulePath[edge.fromModulePath.length - 1] ?? from;
    const mod = graph?.modulesByPath.get(from);
    const parent = mod ? (graph?.pathOfModule.get(mod)?.slice(0, -1) ?? []) : [];
    const key = mod ? moduleDiffId(mod, parent) : from;
    const seen = bucket.get(key);
    if (seen) seen.count += 1;
    else bucket.set(key, { label, count: 1, via: [name], typeName: mod?.typeName });
  }
  return {
    calledBy: peersToConnections(calledBy),
    internal: peersToConnections(internal),
  };
}

/**
 * Details for the entity addressed by `id`, or null when the model no longer
 * knows it — which happens routinely mid-edit, and is the caller's cue to say
 * so rather than render an empty panel.
 */
export function buildEntityDetails(
  model: ResolvedModel | null | undefined,
  graph: DependencyGraph | null,
  id: string,
  opts: BuildDetailsOptions = {},
): EntityDetails | null {
  if (!model) return null;

  const edge = connectionDetails(model, graph, id) ?? portDetails(model, graph, id);
  if (edge) return edge;

  // Who points at this entity. Built structurally by the host (once per
  // assemble), never here — scanning every description per selection would put
  // the whole corpus on the panel's hot path.
  const mentions = (name: string): DetailBacklink[] =>
    (opts.backlinks?.forName(name) ?? []).map((b) => ({
      kind: b.kind,
      label: b.name,
      ...(b.qualifiedName ?? b.path ? { detail: b.qualifiedName ?? b.path } : {}),
    }));

  const aspect = aspectDetails(model, id, mentions);
  if (aspect) return aspect;
  const process = processDetails(model, graph, id, mentions);
  if (process) return process;

  // `Module#Iface` addresses a member: resolve the owner, then the member on it.
  const hash = id.lastIndexOf("#");
  if (hash > 0) {
    const owner = locate(model.modules, id.slice(0, hash), [], null, []);
    const name = id.slice(hash + 1);
    const ownerRel = owner ? connections(graph, owner.path) : null;
    const iface =
      owner &&
      collectInterfaces(owner.mod, ownerRel?.usageByInterface ?? new Map()).find(
        (i) => i.name === name,
      );
    // Through surfaces too — `collectInterfaces` folds surface-owned
    // interfaces into the list above, and the panel mints `Module#name`
    // addresses for every row, so resolving the declaration only on
    // `mod.interfaces` left those rows with empty fields and no location.
    const decl = owner ? findInterfaceDecl(owner.mod, name) : undefined;
    if (!owner || !iface) return null;
    const callers = interfaceCallers(graph, owner.path, name);
    return {
      id,
      kind: "interface",
      label: iface.name,
      qualifiedName: [...owner.path, iface.name].join("."),
      ...(iface.typeName ? { typeName: iface.typeName } : {}),
      ...(iface.description ? { description: iface.description } : {}),
      parent: {
        id: moduleDiffId(owner.mod, owner.parentPath),
        label: owner.mod.name,
        typeName: owner.mod.typeName,
      },
      // The interface's OWN metadata — the web panel lists both, and an
      // interface authored with `aspect intent: "command"` reads as bare
      // without them.
      fields: pairs(decl?.body.fields ?? []),
      aspects: pairs(decl?.body.aspects ?? []),
      // An interface's OWN interfaces are meaningless, but how heavily it is
      // called is the first thing you want to know about one.
      interfaces: [],
      children: [],
      callsOut: [],
      // WHO calls it, not just how often — `usage` counts, this names.
      calledBy: callers.calledBy,
      internal: callers.internal,
      usage: iface.usage,
      processes: [],
      mentionedIn: mentions(iface.name),
      lifecycle: lifecycleOf(decl?.decl.decorations, model.decorationRegistry),
      ...locationField(locationOf(decl?.decl.source)),
      ...(decl?.synthesized ? { synthesized: true } : {}),
    };
  }

  const found = locate(model.modules, id, [], null, []);
  if (!found) return null;
  const { mod, path, parentPath, parent, parentParentPath } = found;
  const body = mod.body;
  const description = body.descriptions.map((d) => d.text).join("\n\n");
  // Team is authored either way round — as a plain field or as the `@@team`
  // aspect — and the inspector shows one line regardless of which was written.
  const team = own(body.fields, "team") ?? own(body.aspects, "team");
  const qualifiedName = path.join(".");
  const rel = connections(graph, path);

  return {
    id,
    kind: "module",
    label: mod.name,
    ...(qualifiedName !== mod.name ? { qualifiedName } : {}),
    typeName: mod.typeName,
    ...(description ? { description } : {}),
    ...(team ? { team: text(team) } : {}),
    ...(parent
      ? {
          parent: {
            id: moduleDiffId(parent, parentParentPath),
            label: parent.name,
            typeName: parent.typeName,
          },
        }
      : {}),
    fields: pairs(body.fields).filter((f) => f.key !== "team"),
    aspects: pairs(body.aspects).filter((a) => a.key !== "team"),
    interfaces: collectInterfaces(mod, rel.usageByInterface),
    children: mod.modules.map((child) => ({
      id: moduleDiffId(child, path),
      label: child.name,
      typeName: child.typeName,
    })),
    callsOut: rel.callsOut,
    calledBy: rel.calledBy,
    internal: rel.internal,
    processes: rel.processes,
    mentionedIn: mentions(mod.name),
    lifecycle: lifecycleOf(mod.decl.decorations, model.decorationRegistry),
    ...locationField(locationOf(mod.decl.source)),
    ...(mod.synthesized ? { synthesized: true } : {}),
    ...(mod.unnamed ? { unnamed: true } : {}),
  };
}
